import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Transcript, TranscriptSegment } from "@/types";
import { getAudioSignedUrl } from "@/lib/storage";
import { getTranscriptText, updateTranscriptText } from "@/lib/transcript";

interface TranscriptViewProps {
  transcript: Transcript;
  audioPath?: string | null;
  onEdited?: (newText: string) => void;
}

function formatTimestamp(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function TranscriptView({ transcript, audioPath, onEdited }: TranscriptViewProps) {
  const { t } = useTranslation();
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!audioPath) {
      setAudioUrl(null);
      return;
    }
    getAudioSignedUrl(audioPath).then((url) => {
      if (!cancelled) setAudioUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [audioPath]);

  const segments = useMemo(
    () => (transcript.segments?.length ? transcript.segments : null),
    [transcript.segments]
  );

  const currentText = getTranscriptText(transcript);
  const isEdited = transcript.edited_text != null;

  const startEditing = () => {
    setDraft(currentText);
    setEditing(true);
    setError(null);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraft("");
    setError(null);
  };

  const saveEditing = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await updateTranscriptText(transcript.id, draft);
      onEdited?.(draft.trim());
      setEditing(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const header = (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <h3 className="text-redi-text text-lg font-semibold">
          {t("transcript.title")}
        </h3>
        {isEdited && !editing && (
          <span className="px-2 py-0.5 rounded-full bg-redi-primary/20 text-redi-primary text-xs">
            {t("transcript.edited")}
          </span>
        )}
      </div>
      {!editing && (
        <button
          onClick={startEditing}
          className="flex items-center gap-1 px-2 py-1 text-redi-muted hover:text-redi-text text-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          {t("transcript.edit")}
        </button>
      )}
    </div>
  );

  if (editing) {
    return (
      <div className="mb-6">
        {header}
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="block w-full max-w-full box-border bg-redi-surface text-redi-text text-base leading-6 rounded-xl p-4 focus:outline-none focus:ring-2 focus:ring-redi-primary"
          rows={12}
          autoFocus
        />
        {error && (
          <p className="text-redi-error text-sm mt-2">{error}</p>
        )}
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={cancelEditing}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-redi-surface text-redi-muted text-sm hover:text-redi-text disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={saveEditing}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-redi-primary text-white text-sm font-semibold hover:bg-redi-primary/80 disabled:opacity-50"
          >
            {saving ? "..." : t("common.save")}
          </button>
        </div>
      </div>
    );
  }

  if (audioUrl && segments && !isEdited) {
    return (
      <div className="mb-6">
        {header}
        <PlayableTranscriptBody
          transcript={transcript}
          segments={segments}
          audioUrl={audioUrl}
        />
      </div>
    );
  }

  return (
    <div className="mb-6">
      {header}
      <div className="bg-redi-surface rounded-xl p-5">
        <p className="text-redi-text text-sm whitespace-pre-wrap leading-6">
          {currentText}
        </p>
        <div className="flex gap-4 mt-4 pt-3 border-t border-white/5">
          {transcript.word_count != null && (
            <span className="text-redi-muted text-xs">
              {t("transcript.wordCount", { count: transcript.word_count })}
            </span>
          )}
          {transcript.confidence != null && (
            <span className="text-redi-muted text-xs">
              {t("transcript.confidence", {
                value: Math.round(transcript.confidence * 100),
              })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function PlayableTranscriptBody({
  transcript,
  segments,
  audioUrl,
}: {
  transcript: Transcript;
  segments: TranscriptSegment[];
  audioUrl: string;
}) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const activeIndex = useMemo(() => {
    if (!hasInteracted) return -1;
    return segments.findIndex(
      (seg) => currentTime >= seg.start && currentTime < seg.end
    );
  }, [segments, currentTime, hasInteracted]);

  const handleSegmentClick = async (segment: TranscriptSegment) => {
    setHasInteracted(true);
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = segment.start;
    try {
      await audio.play();
    } catch {}
  };

  const togglePlay = async () => {
    setHasInteracted(true);
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      try {
        await audio.play();
      } catch {}
    } else {
      audio.pause();
    }
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      <div className="bg-redi-surface rounded-xl p-4 mb-3 flex items-center gap-3">
        <button
          onClick={togglePlay}
          className="w-12 h-12 rounded-full bg-redi-primary flex items-center justify-center hover:bg-redi-primary/80"
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>
        <div className="flex-1">
          <p className="text-redi-text text-sm font-mono">
            {formatTimestamp(currentTime)}
            {duration > 0 ? ` / ${formatTimestamp(duration)}` : ""}
          </p>
          <p className="text-redi-muted text-xs mt-1">
            {t("transcript.playerHint")}
          </p>
        </div>
      </div>

      <div className="bg-redi-surface rounded-xl p-2">
        {segments.map((segment, idx) => {
          const isActive = idx === activeIndex;
          return (
            <button
              key={idx}
              onClick={() => handleSegmentClick(segment)}
              className={`flex w-full p-2 rounded-lg text-left transition-colors ${
                isActive ? "bg-redi-primary/15" : "hover:bg-white/5"
              }`}
            >
              <span
                className={`text-xs font-mono w-12 mt-0.5 flex-shrink-0 ${
                  isActive ? "text-redi-primary" : "text-redi-muted"
                }`}
              >
                {formatTimestamp(segment.start)}
              </span>
              <span className="flex-1 block">
                {segment.speaker && (
                  <span className="block text-redi-primary text-xs font-semibold mb-0.5">
                    {t("transcript.speaker", { id: segment.speaker })}
                  </span>
                )}
                <span
                  className={`block text-sm leading-6 ${
                    isActive ? "text-redi-text font-medium" : "text-redi-text/90"
                  }`}
                >
                  {segment.text}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex gap-4 mt-2">
        {transcript.word_count != null && (
          <span className="text-redi-muted text-xs">
            {t("transcript.wordCount", { count: transcript.word_count })}
          </span>
        )}
        {transcript.confidence != null && (
          <span className="text-redi-muted text-xs">
            {t("transcript.confidence", {
              value: Math.round(transcript.confidence * 100),
            })}
          </span>
        )}
      </div>
    </>
  );
}
